import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AddAlertOutlinedIcon from '@mui/icons-material/AddAlertOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: '#ffffff',
  borderRadius: 2,
  borderColor: 'primary.main',
  borderWidth: 2,
  borderStyle: 'solid',
  boxShadow: 24,
  overflow: 'hidden',
  width: '92vw',
  maxWidth: 640,
  maxHeight: '90vh',
};

// Tarjeta de opción del modal de gestión.
const ManagementCard = ({ icon, iconColor, title, description, onClick, disabled }) => (
  <button
    type='button'
    onClick={onClick}
    disabled={disabled}
    className='group flex flex-col items-start gap-3 rounded-xl border border-primary/15 bg-white p-4 text-left transition duration-150 hover:border-primary hover:bg-background hover:shadow-solid-5 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-primary/15 disabled:hover:bg-white disabled:hover:shadow-none'>
    <span
      className='flex h-11 w-11 items-center justify-center rounded-lg text-white'
      style={{ backgroundColor: iconColor }}>
      {icon}
    </span>
    <span className='font-semibold text-primary'>{title}</span>
    <span className='text-sm text-muted-foreground'>{description}</span>
  </button>
);

const CourseManagementModal = ({ open, setOpen, course, onNotify }) => {
  const navigate = useNavigate();

  const handleClose = () => setOpen(false);

  const go = path => {
    setOpen(false);
    navigate(path);
  };

  const options = [
    {
      key: 'units',
      title: 'Unidades de Aprendizaje',
      description: 'Gestiona las unidades y contenidos del curso',
      icon: <CollectionsBookmarkOutlinedIcon />,
      iconColor: '#004591',
      onClick: () => go(`/courses/${course.id}/learning_units`),
    },
    {
      key: 'students',
      title: 'Estudiantes',
      description: 'Administra la lista de estudiantes inscritos',
      icon: <GroupsOutlinedIcon />,
      iconColor: '#7ed321',
      onClick: () => go(`/courses/${course.id}/my_students`),
      hidden: Boolean(course.for_all),
    },
    {
      key: 'notify',
      title: 'Notificar',
      description: 'Envía notificaciones y avisos a los estudiantes',
      icon: <AddAlertOutlinedIcon />,
      iconColor: '#4a90e2',
      onClick: () => {
        setOpen(false);
        onNotify(course);
      },
    },
    {
      key: 'paths',
      title: 'Rutas de Aprendizaje',
      description: 'Define rutas de aprendizaje personalizadas',
      icon: <RouteOutlinedIcon />,
      iconColor: '#5a6b8c',
      onClick: () => go(`/courses/${course.id}/learning-paths`),
    },
  ].filter(option => !option.hidden);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby='course-management-title'
      aria-describedby='course-management-description'>
      <Box sx={modalStyle}>
        {/* Encabezado */}
        <div className='relative bg-gradient-to-r from-primary to-secondary px-6 py-5 text-white'>
          <IconButton
            aria-label='Cerrar'
            onClick={handleClose}
            size='small'
            sx={{ position: 'absolute', top: 8, right: 8, color: '#ffffff' }}>
            <CloseIcon fontSize='small' />
          </IconButton>
          <p className='text-xs font-semibold uppercase tracking-wider text-white/80'>
            Gestión del curso
          </p>
          <h2 id='course-management-title' className='mt-1 text-xl font-bold leading-tight'>
            {course.name}
          </h2>
          <p id='course-management-description' className='mt-1 text-sm text-white/90'>
            Año: {course.year} · Semestre: {course.semester}
          </p>
        </div>

        {/* Opciones */}
        <div className='grid grid-cols-1 gap-4 p-6 sm:grid-cols-2'>
          {options.map(({ key, hidden, ...cardProps }) => (
            <ManagementCard key={key} {...cardProps} />
          ))}
        </div>

        {/* Pie */}
        <div className='flex justify-end border-t border-primary/10 px-6 py-4'>
          <button
            type='button'
            onClick={handleClose}
            className='rounded-full bg-muted px-6 py-2 font-semibold text-primary transition duration-150 hover:bg-muted-foreground/20'>
            Cancelar
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default CourseManagementModal;
